import React from 'react';
import styled from 'styled-components';

import useTranslation from 'hooks/use-translation';
import MetaTags from 'components/meta-tags';
import { GoogleLogin } from 'components/social-login/google-login';
import { AppleLogin } from 'components/social-login/apple-login';
import { RibbonStarV2 } from 'src/assets/ribbon-star-v2';
import type { LoginMethodSelectionProps } from '../types';

/**
 * Login method selection component - the second step in the flow
 */
export function LoginMethodSelection({ onContinueWithEmail }: LoginMethodSelectionProps): JSX.Element {
  const { t } = useTranslation();

  const redirectUrl = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <>
      <MetaTags
        title={t('enterprise.loginMethod.title', 'Choose Login Method')}
        description={t('enterprise.loginMethod.description', 'Select how you want to sign up')}
      />

      <LoginMethodContainer>
        <LoginMethodCard>
          <IconContainer>
            <RibbonStarV2 />
          </IconContainer>

          <LoginMethodHeader>
            <LoginMethodTitle>
              {t('enterprise.loginMethod.headerTitle', 'Almost there! Set up your account')}
            </LoginMethodTitle>
            <LoginMethodSubtitle>
              {t(
                'enterprise.loginMethod.headerSubtitle',
                'Choose how you would like to sign up and start earning rewards today!'
              )}
            </LoginMethodSubtitle>
          </LoginMethodHeader>

          <SocialLoginButtons>
            <SocialButtonWrapper>
              <GoogleLogin redirectUrl={redirectUrl} publicContextSessionId={null} />
            </SocialButtonWrapper>

            <SocialButtonWrapper>
              <AppleLogin redirectUrl={redirectUrl} publicContextSessionId={null} />
            </SocialButtonWrapper>
          </SocialLoginButtons>

          <EmailLoginLink onClick={onContinueWithEmail}>
            {t('enterprise.loginMethod.continueWithEmail', 'Continue with email')}
          </EmailLoginLink>
        </LoginMethodCard>
      </LoginMethodContainer>
    </>
  );
}

// Styled Components for Login Method Selection
const LoginMethodContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  padding: 1rem;
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;

  svg {
    width: 80px;
    height: 80px;
  }
`;

const LoginMethodCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  padding: 3rem 2rem;
  width: 100%;
  max-width: 500px;
  text-align: center;
`;

const LoginMethodHeader = styled.div`
  margin-bottom: 2.5rem;
`;

const LoginMethodTitle = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 1rem 0;
  line-height: 1.2;
`;

const LoginMethodSubtitle = styled.p`
  font-size: 1rem;
  color: #4b5563;
  margin: 0;
  line-height: 1.5;
`;

const SocialLoginButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SocialButtonWrapper = styled.div`
  width: 100%;

  /* Override social login component styles to match design */
  button {
    width: 100% !important;
    padding: 1rem !important;
    border-radius: 50px !important;
    border: 1px solid #e5e7eb !important;
    background: white !important;
    color: #374151 !important;
    font-size: 1rem !important;
    font-weight: 500 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 0.75rem !important;
    transition: all 0.2s ease !important;

    &:hover {
      background: #f9fafb !important;
      border-color: #d1d5db !important;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
  }
`;

const EmailLoginLink = styled.button`
  background: none;
  border: none;
  color: #3b82f6;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;
